import { rem } from "@certn/cascadia";
import {
  CascadiaMiniIcon,
  lineHeights,
  sizes,
  textStyles,
  weights,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useRef } from "react";
import { AriaTextFieldProps, useTextField } from "react-aria";

import { useColors } from "../../../hooks/useColors";
import {
  FieldErrors,
  FieldErrorsProps,
} from "../_assets/input-wrapper/FieldErrors";
import { FormFeedback } from "../_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../_assets/input-wrapper/FormLabel";
import { FormText } from "../_assets/input-wrapper/FormText";
import { InputWrapperProps } from "../_assets/input-wrapper/InputWrapper";

export interface InputGroupProps extends AriaTextFieldProps, InputWrapperProps {
  name: string;
  dropdown: React.ReactNode;
  info?: React.ReactNode;
  icon?: CascadiaMiniIcon;
  clearable?: boolean;
  isInvalid?: boolean;
  errors?: FieldErrorsProps["children"];
  successMessage?: string;
  testID?: string;
}

export function InputGroup(props: InputGroupProps) {
  const {
    label,
    validationState,
    placeholder,
    dropdown,
    testID = "input-group",
    isDisabled,
    clearable,
    icon,
  } = props;
  const ref = useRef<HTMLInputElement>(null);
  const { labelProps, inputProps, descriptionProps } = useTextField(props, ref);
  const error = validationState === "invalid";
  const values = textStyles["input/text-field"];
  const colors = useColors();

  return (
    <div data-testid="input-group-wrapper">
      <FormLabel {...labelProps} isRequired info={props.info}>
        {label}
      </FormLabel>
      <div className={styles.container}>
        <div className={styles.displayFlex}>{dropdown}</div>
        <div className={css(styles.relativeBlock)}>
          <input
            {...inputProps}
            placeholder={validationState === "invalid" ? "" : placeholder}
            className={css(styles.input, {
              "&:invalid": {
                border: `1px solid ${colors["border-danger-input"]}`,
              },
              "::placeholder": {
                color: "var(--text-placeholder)",
              },
              fontSize: rem(sizes[values.size]),
              lineHeight: rem(lineHeights[values.lineHeight]),
              fontWeight: weights[values.weight],
              backgroundColor: isDisabled
                ? "var(--background-input-disabled)"
                : "var(--background-input)",
              boxShadow: error
                ? `inset 0 0 0 1px ${colors["border-danger-input"]}`
                : isDisabled
                  ? `inset 0 0 0 1px ${colors["background-input-disabled"]}`
                  : `inset 0 0 0 1px ${colors["border-strong"]}`,
              paddingRight: clearable ? "var(--space-9)" : "var(--space-3)",
              paddingLeft: icon ? "var(--space-9)" : "var(--space-3)",
              border: isDisabled
                ? `1px solid ${colors["border-input-disabled"]}`
                : "none",
              color: isDisabled ? "var(--text-disabled)" : "var(--text-base)",
              "&:hover:not(:focus)": props.isDisabled
                ? { borderColor: undefined }
                : !error
                  ? {
                      borderColor: "var(--border-input-active)",
                    }
                  : null,
              "&:focus": error
                ? css([
                    {
                      outline: "0",
                      borderColor: "var(--border-danger-input)",
                      background: "var(--background-base)",
                      boxShadow: "var(--focus-input-error)",
                    },
                  ])
                : css([
                    {
                      outline: "0",
                      borderColor: "var(--border-input-active)",
                      boxShadow: "var(--focus-input)",
                    },
                  ]),
            })}
            ref={ref}
            data-testid={testID}
          />
        </div>
      </div>
      <FormText {...descriptionProps}>{props.description}</FormText>
      <FieldErrors>{props.errors}</FieldErrors>
      <FormFeedback type="valid">{props.successMessage}</FormFeedback>
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    gap: "var(--space-2)",
  }),
  displayFlex: css({
    display: "flex",
  }),
  relativeBlock: css({
    position: "relative",
    flexGrow: 1,
  }),
  input: css({
    WebkitAppearance: "none",
    width: "100%",
    boxSizing: "border-box",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
    borderRadius: "var(--border-radius-2)",
    fontFamily: "var(--font-family-default)",
  }),
};
